import React, { useEffect } from 'react';
import styles from './DashboardPostalCodeFilter.module.scss';
import DeliveryDashboardSideCard from '../../../DeliveryDashboardSideCard';
import PIEText from '@src/shared/components/PIEText';
import { IconCheck, IconSortAscending } from '@justeattakeaway/pie-icons-react';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import { IOrder } from '../../../../models/model-interface';
import PIEChip from '@src/shared/components/PIEChip';
import { Badge } from 'snacks-design-system';
import { useTranslation } from 'react-i18next';
import { usePostalCodeFilterTracking } from './tracking/use-dashboard-postalcode-filter-tracking';
interface IComponentProps {
  orderList: Array<IOrder>;
  initialSelectedPostCodes?: Array<string>;
  onSelectedPostCodesChange?: (selectedPostCodes: Array<string>) => void;
  onUniquePostCodesChange?: (uniquePostCodes: Array<string>) => void;
}

const DashboardPostalCodeFilter: React.FC<IComponentProps> = ({
  orderList = [],
  onSelectedPostCodesChange = () => {},
  onUniquePostCodesChange = () => {},
  initialSelectedPostCodes = [],
}) => {
  const [postCodes, setPostCodes] = React.useState<Array<string>>([]);
  const [selectedPostCodes, setSelectedPostCodes] =
    React.useState<Array<string>>(initialSelectedPostCodes);
  const { t } = useTranslation();
  const { trackPostalCodeFilterToggle, trackPostalCodeSelection } = usePostalCodeFilterTracking();

  useEffect(() => {
    const uniquePostCodes = new Set(orderList.map((order) => order.customer.address.postcode));
    setPostCodes(Array.from(uniquePostCodes));
    const updatedSelectedPostCodes = selectedPostCodes.filter((code) =>
      Array.from(uniquePostCodes).includes(code)
    );
    if (updatedSelectedPostCodes.length !== selectedPostCodes.length) {
      setSelectedPostCodes(updatedSelectedPostCodes);
    }
  }, [orderList]);

  useEffect(() => {
    onSelectedPostCodesChange(selectedPostCodes);
  }, [selectedPostCodes]);

  useEffect(() => {
    onUniquePostCodesChange(postCodes);
  }, [postCodes]);

  return (
    (postCodes.length >= 2 && (
      <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
        <DeliveryDashboardSideCard
          withMargin={false}
          isAbsolute={false}
          fullWidth={true}
          showBackButton={false}
          isOpenInitial={selectedPostCodes.length ? true : false}
          onTogglerRef={() => {
            trackPostalCodeFilterToggle();
          }}
          header={
            <PIEText
              size="var(--xds-font-size-16)"
              align="left"
              text={
                <div style={{ position: 'relative' }}>
                  <IconSortAscending className={styles.header_icon} size={'s'} />{' '}
                  {t('order.postcode_filter_title')}
                  {selectedPostCodes.length > 0 && (
                    <Badge qaSelector="dashboard-postal-code-filter-bubble">
                      {selectedPostCodes.length}
                    </Badge>
                  )}
                </div>
              }
              fontWeight="var(--xds-font-weight-regular)"
            ></PIEText>
          }
          noSeparator={true}
        >
          <div className={styles.postcodes_container}>
            {postCodes.map((postcode, i) => (
              <PIEChip
                key={i}
                className={styles.filter_item}
                isSelected={selectedPostCodes.includes(postcode)}
                onClick={() => {
                  trackPostalCodeSelection();
                  if (selectedPostCodes.includes(postcode)) {
                    setSelectedPostCodes(selectedPostCodes.filter((code) => code !== postcode));
                  } else {
                    setSelectedPostCodes([...selectedPostCodes, postcode]);
                  }
                }}
              >
                {selectedPostCodes.includes(postcode) ? (
                  <IconCheck className={styles.check_icon} size="xs" />
                ) : null}
                {postcode}
              </PIEChip>
            ))}
          </div>
        </DeliveryDashboardSideCard>
      </PIEComponentUtil>
    )) || <></>
  );
};

export default DashboardPostalCodeFilter;
